import { map } from 'lodash-es'
import { ChangeEvent, FC, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'

import { Icon } from '@/common/components/Icon'
import { AlertRatio } from '@/components/AlertRatio'
import SelectField from '@/components/select-field'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import FormGroup from '@/core/components/FormGroup'
import { Input } from '@/components/ui/input'
import { Coin } from '../../../common/models'
import { IGroup } from '../../../common/models/groups'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { deleteCoin } from '../../../core/services/currencies'
import { uploadImage } from '../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { ICurrencyValues } from './CurrenciesTab'

type Props = {
  currency: Coin
  groups: IGroup[]
  control: Control<ICurrencyValues>
  errors: FieldErrors<ICurrencyValues>
  register: UseFormRegister<ICurrencyValues>
  index: number
  remove: UseFieldArrayRemove
  watch: UseFormWatch<ICurrencyValues>
  setValue: UseFormSetValue<ICurrencyValues>
  setCoins: (c: Coin[]) => void
  coins: Coin[]
  isSubmitting?: boolean
}

export const Currency: FC<Props> = ({
  errors,
  currency,
  register,
  groups,
  index,
  setCoins,
  control,
  remove,
  watch,
  setValue,
  coins,
  isSubmitting,
}): JSX.Element => {
  let iName = ''
  if (currency.iconLink) {
    const url = new URL(currency.iconLink)
    const i = url?.pathname?.lastIndexOf('/') + 1
    iName = url?.pathname?.substring(i)
  }
  const [imageName, setImageName] = useState<string>(iName)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [image, setImage] = useState<string>(currency.iconLink)
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = useState<number>()
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const name = watch(`currencies.${index}.name`)
  const id = watch(`currencies.${index}.id`)
  const festivalGroupId = watch(`currencies.${index}.festivalGroupId`)

  return (
    <>
      <Card className="mb-4 bg-gradient-4 border border-monochrome-120 p-7.5">
        <div className="flex justify-between items-center">
          <h3 className="text-title-s text-accent-1">{name}</h3>
          <Button
            variant="destructive"
            type="button"
            className="w-[154px]"
            onClick={async () => {
              setDeleteIndex(index)
              setDeleteConfirmation(true)
            }}
          >
            Delete
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
          </Button>
        </div>
        <div className="space-y-4">
          <FormGroup
            label="Group Name"
            error={errors?.currencies?.[index]?.name?.message}
          >
            <Input
              isInvalid={!!errors?.currencies?.[index]?.name?.message}
              {...register(`currencies.${index}.name`)}
              type="text"
              placeholder="Currency Name"
            />
          </FormGroup>
          <div className="space-y-2 w-full">
            <FormUploadPreview
              formGroupProps={{
                className: 'mt-2',
                labelClassName: 'mb-2',
              }}
              control={
                <Input
                  multiple={false}
                  accept="image/*"
                  isInvalid={
                    !!errors?.currencies?.[index]?.iconLink?.message ||
                    invalidPhoto ||
                    isErrorRatio
                  }
                  name={`currencies.${index}.icon`}
                  onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                    const file = e?.target?.files?.[0]
                    if (file) {
                      setInvalidPhoto(false)
                      setIsErrorRatio(false)
                      if (file.size > 1_000_000) {
                        setInvalidPhoto(true)
                        setIsError(true)
                        setShowMessageBox(true)
                        setMessage('The photo is too large (1 MB)')
                        return
                      }
                      const data = await uploadImage(
                        file,
                        setIsUploading,
                        setIsError,
                        setShowMessageBox,
                        setMessage,
                        setIsErrorRatio,
                        'GROUP_CONFIG_CURRENCY',
                        false,
                      )
                      if (data.photoUrl) {
                        setImage(data.photoUrl)
                        setImageName(file.name)
                        setValue(
                          `currencies.${index}.iconLink`,
                          data.photoKey,
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                          },
                        )
                      }
                    }
                  }}
                  type="file"
                />
              }
              imageName={imageName}
              direction="row"
              isUploading={isUploading}
              url={image}
              onRemove={() => {
                setImage('')
                setImageName('')
                setValue(`currencies.${index}.iconLink`, '')
              }}
              imageStyle={{
                objectFit: 'contain',
              }}
              label="Custom Currency Icon (64X64)"
              previewLabel="Custom Currency Icon (64X64)"
            />
            {isErrorRatio && <AlertRatio />}
          </div>
          <Card className="mt-2 p-4 bg-monochrome-3-60 border border-monochrome-120 space-y-4">
            <FormGroup
              label="Select Group"
              error={errors?.currencies?.[index]?.festivalGroupId?.message}
            >
              <Controller
                name={`currencies.${index}.festivalGroupId`}
                control={control}
                render={({ field }) => (
                  <SelectField
                    outline
                    error={
                      errors?.currencies?.[index]?.festivalGroupId?.message
                    }
                    triggerClassName="h-[44px] rounded-2xl"
                    placeholder="Select Group"
                    options={map(groups, (group) => ({
                      value: group.id,
                      label: group.name,
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </FormGroup>

            <div className="flex w-full items-center justify-between gap-4">
              <FormGroup
                label="Custom Currency Amount"
                tooltip="Custom Currency Amount"
                labelClassName="mb-2"
                className="flex-1"
              >
                <Input
                  value={1}
                  type="number"
                  disabled
                  placeholder="Custom Currency Amount"
                />
              </FormGroup>
              <span className="mt-8">=</span>
              <FormGroup
                label={
                  <span className="text-monochrome-1 text-body-medium mb-2">
                    EUR{' '}
                    <span className="text-monochrome-2">
                      (FESTIVAL GROUP MAIN CURRENCY)
                    </span>
                  </span>
                }
                tooltip="EUR (FESTIVAL GROUP MAIN CURRENCY)"
                error={errors?.currencies?.[index]?.exchangeRate?.message}
                className="flex-1"
              >
                <Input
                  isInvalid={
                    !!errors?.currencies?.[index]?.exchangeRate?.message
                  }
                  {...register(`currencies.${index}.exchangeRate`)}
                  type="number"
                  placeholder="Enter Conversion Rate"
                />
              </FormGroup>
            </div>
          </Card>
        </div>

        <div className="flex gap-2 justify-end mt-4 border-t border-monochrome-120 pt-4 [&>*]:w-[154px]">
          <Button variant="secondary" size="sm" type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            name={id}
            form={`currencies-${index}-form`}
            size="sm"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Currency'}
        deleteFunc={async () => {
          if (id) {
            const resp = await deleteCoin(id)
            if (resp.status === 204) {
              setDeleteConfirmation(false)
            }
          }
          if (deleteIndex) {
            remove(deleteIndex)
            setCoins(coins.splice(deleteIndex, 1))
          }
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
